import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { FileText, Mic, Image, Video, User, Layers } from 'lucide-react'
import { usePatientReports } from '../hooks/use-patient-detail'
import { format, isValid, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { RELATIONSHIP_LABELS } from '@/features/shared/constants/relationships'
import type { RelationshipType } from '@/features/shared/types/patient.types'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

function formatReportDate(dateValue: string | null | undefined): { date: string; time: string } {
  if (!dateValue) {
    return { date: 'Data não disponível', time: '' }
  }

  const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue)

  if (!isValid(date)) {
    return { date: 'Data inválida', time: '' }
  }

  return {
    date: format(date, "d 'de' MMMM, yyyy", { locale: ptBR }),
    time: format(date, 'HH:mm'),
  }
}

interface PatientReportsTabProps {
  patientId: string
}

const sentimentColors: Record<string, string> = {
  positive: 'bg-green-100 text-green-800',
  neutral: 'bg-gray-100 text-gray-800',
  negative: 'bg-red-100 text-red-800',
  mixed: 'bg-yellow-100 text-yellow-800',
}

const sentimentLabels: Record<string, string> = {
  positive: 'Positivo',
  neutral: 'Neutro',
  negative: 'Negativo',
  mixed: 'Misto',
}

const reportTypeConfig: Record<string, { icon: typeof FileText; label: string; color: string }> = {
  text: { icon: FileText, label: 'Texto', color: 'bg-blue-100 text-blue-700' },
  audio: { icon: Mic, label: 'Áudio', color: 'bg-purple-100 text-purple-700' },
  image: { icon: Image, label: 'Imagem', color: 'bg-green-100 text-green-700' },
  video: { icon: Video, label: 'Vídeo', color: 'bg-orange-100 text-orange-700' },
}

interface ReportMetadata {
  transcribedContent?: string
  imageAnalyses?: string[]
  mediaAssets?: ReportMediaAsset[]
  whatsappAggregation?: {
    messageCount?: number
    messageTypes?: string[]
  }
  analysis?: {
    sentiment?: { label?: string; score?: number }
    keyEvents?: string[]
    concerns?: string[]
  }
  wordCount?: number
  hasMedia?: boolean
  analyzedAt?: string
}

interface ReportMediaAsset {
  bucket: string
  path: string
  url?: string
  type: string
  mimeType: string
  originalName?: string
}

interface ReportReporter {
  id: string
  relationship: string
  profiles: {
    full_name: string
  }
}

function getReporterName(reporter: ReportReporter | null | undefined): string | null {
  if (!reporter?.profiles?.full_name) return null
  const name = reporter.profiles.full_name
  const relationshipLabel =
    RELATIONSHIP_LABELS[reporter.relationship as RelationshipType] ?? reporter.relationship
  return `${name} (${relationshipLabel})`
}

export function PatientReportsTab({ patientId }: PatientReportsTabProps) {
  const { data: reports, isLoading } = usePatientReports(patientId)

  if (isLoading) {
    return <ReportsSkeleton />
  }

  if (!reports || reports.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">Nenhum relatório encontrado</p>
        <p className="text-sm mt-1">
          Os relatórios aparecerão aqui quando forem enviados via WhatsApp
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {reports.map((report) => {
        const reportType = (report.report_type as string) || 'text'
        const typeConfig = reportTypeConfig[reportType] ?? reportTypeConfig.text!
        const TypeIcon = typeConfig!.icon
        const metadata = (report.metadata as ReportMetadata) || {}
        const transcription = metadata.transcribedContent
        const imageAnalyses = metadata.imageAnalyses ?? []
        const mediaAssets = metadata.mediaAssets ?? []
        const messageCount = metadata.whatsappAggregation?.messageCount ?? 1
        const reporter = report.reporter as ReportReporter | null | undefined
        const reporterName = getReporterName(reporter)

        return (
          <Card key={report.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`p-2 rounded-lg ${typeConfig!.color.split(' ')[0]}`}>
                    <TypeIcon className={`h-5 w-5 ${typeConfig!.color.split(' ')[1]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {(() => {
                        const { date, time } = formatReportDate(report.created_at)
                        return (
                          <>
                            <span className="text-sm font-medium">{date}</span>
                            {time && <span className="text-sm text-muted-foreground">{time}</span>}
                          </>
                        )
                      })()}
                      <Badge className={typeConfig!.color}>
                        {typeConfig!.label}
                      </Badge>
                      {messageCount > 1 && (
                        <Badge variant="outline" className="gap-1">
                          <Layers className="h-3 w-3" />
                          {messageCount} mensagens
                        </Badge>
                      )}
                      {report.sentiment && (
                        <Badge
                          className={sentimentColors[report.sentiment] ?? 'bg-gray-100 text-gray-800'}
                        >
                          {sentimentLabels[report.sentiment] ?? report.sentiment}
                        </Badge>
                      )}
                    </div>

                    {/* Reporter name */}
                    {reporterName && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Relatado por <span className="font-medium text-gray-700">{reporterName}</span>
                        </span>
                      </div>
                    )}

                    {/* Content: prefer transcription for audio, fallback to content */}
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {reportType === 'audio' && transcription
                        ? transcription
                        : report.content}
                    </p>

                    {imageAnalyses.length > 0 && (
                      <div className="mt-3 rounded-md border bg-muted/30 p-3">
                        <p className="mb-1 text-xs font-medium text-muted-foreground">
                          Análise da imagem
                        </p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {imageAnalyses.join('\n\n')}
                        </p>
                      </div>
                    )}

                    {mediaAssets.length > 0 && (
                      <ReportMediaAssets assets={mediaAssets} />
                    )}

                    {report.source && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Fonte: {report.source}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}

      {reports.length >= 20 && (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            Mostrando os 20 relatórios mais recentes
          </p>
        </div>
      )}
    </div>
  )
}

function ReportMediaAssets({ assets }: { assets: ReportMediaAsset[] }) {
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false

    async function loadSignedUrls() {
      const entries = await Promise.all(
        assets.map(async (asset) => {
          const { data } = await supabase.storage
            .from(asset.bucket)
            .createSignedUrl(asset.path, 3600)

          return [asset.path, data?.signedUrl ?? ''] as const
        }),
      )

      if (!cancelled) {
        setSignedUrls(Object.fromEntries(entries.filter(([, url]) => url)))
      }
    }

    void loadSignedUrls()

    return () => {
      cancelled = true
    }
  }, [assets])

  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-2">
      {assets.map((asset) => {
        const signedUrl = signedUrls[asset.path]
        if (!signedUrl) {
          return (
            <div key={asset.path} className="h-24 rounded-md border bg-muted animate-pulse" />
          )
        }

        if (asset.type === 'image') {
          return (
            <a
              key={asset.path}
              href={signedUrl}
              target="_blank"
              rel="noreferrer"
              className="block overflow-hidden rounded-md border bg-muted"
            >
              <img
                src={signedUrl}
                alt={asset.originalName || 'Imagem do relato'}
                className="h-40 w-full object-cover"
              />
            </a>
          )
        }

        if (asset.type === 'audio') {
          return (
            <audio key={asset.path} controls className="w-full">
              <source src={signedUrl} type={asset.mimeType} />
            </audio>
          )
        }

        return null
      })}
    </div>
  )
}

function ReportsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <div className="flex-1">
                <Skeleton className="h-4 w-48 mb-2" />
                <Skeleton className="h-16 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
